import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// Async thunks
export const fetchMedia = createAsyncThunk(
  'media/fetchMedia',
  async ({ params = {} } = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(`/media?${queryParams}`);
    return response.data;
  }
);

export const uploadMedia = createAsyncThunk(
  'media/uploadMedia',
  async ({ file }) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`/media/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
);

export const uploadMultipleMedia = createAsyncThunk(
  'media/uploadMultipleMedia',
  async ({ files }) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await axios.post(`/media/upload-multiple`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
);

export const storeAIImage = createAsyncThunk(
  'media/storeAIImage',
  async ({ imageUrl, prompt, alt, description }) => {
    const response = await axios.post(`/media/ai-generated`, { imageUrl, prompt, alt, description });
    return response.data;
  }
);

export const updateMedia = createAsyncThunk(
  'media/updateMedia',
  async ({ id, updates }) => {
    const response = await axios.put(`/media/${id}`, updates);
    return response.data;
  }
);

export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async ({ id }) => {
    await axios.delete(`/media/${id}`);
    return id;
  }
);

// Note: We no longer load from localStorage on initialization
// Media is always fetched fresh from the API

const mediaSlice = createSlice({
  name: 'media',
  initialState: {
    media: [], // Start with empty array, always fetch from API
    pagination: {
      current: 1,
      pages: 1,
      total: 0
    },
    storage: null, // Storage limit info from API
    loading: false,
    uploading: false,
    error: null,
    selectedMedia: [],
    filters: {
      type: 'all',
      search: '',
      page: 1,
      limit: 24
    }
  },
  reducers: {
    setFilters: (state, action) => {
      // Reset to page 1 whenever a non-pagination filter changes so the
      // user does not end up on an out-of-range page after re-filtering.
      const next = { ...state.filters, ...action.payload };
      const resetsPage =
        action.payload &&
        (Object.prototype.hasOwnProperty.call(action.payload, 'type') ||
          Object.prototype.hasOwnProperty.call(action.payload, 'search')) &&
        !Object.prototype.hasOwnProperty.call(action.payload, 'page');
      if (resetsPage) next.page = 1;
      state.filters = next;
    },
    setPage: (state, action) => {
      state.filters.page = Math.max(1, parseInt(action.payload, 10) || 1);
    },
    clearFilters: (state) => {
      state.filters = {
        type: 'all',
        search: '',
        page: 1,
        limit: state.filters.limit || 24
      };
    },
    setSelectedMedia: (state, action) => {
      state.selectedMedia = action.payload;
    },
    toggleMediaSelection: (state, action) => {
      const mediaId = action.payload;
      const index = state.selectedMedia.indexOf(mediaId);
      if (index > -1) {
        state.selectedMedia = state.selectedMedia.filter(id => id !== mediaId);
      } else {
        state.selectedMedia = [...state.selectedMedia, mediaId];
      }
    },
    clearSelectedMedia: (state) => {
      state.selectedMedia = [];
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch media
      .addCase(fetchMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedia.fulfilled, (state, action) => {
        state.loading = false;
        state.media = action.payload.media;
        state.pagination = action.payload.pagination;
        state.storage = action.payload.storage || null;
        // Don't save media data to localStorage for security
      })
      .addCase(fetchMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Upload media
      .addCase(uploadMedia.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.uploading = false;
        state.media = [action.payload, ...state.media];
        state.pagination.total += 1;
        // Don't save media data to localStorage for security
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.error.message;
      })
      
      // Upload multiple media
      .addCase(uploadMultipleMedia.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadMultipleMedia.fulfilled, (state, action) => {
        state.uploading = false;
        state.media = [...action.payload, ...state.media];
        state.pagination.total += action.payload.length;
        // Don't save media data to localStorage for security
      })
      .addCase(uploadMultipleMedia.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.error.message;
      })
      
      // Store AI image
      .addCase(storeAIImage.pending, (state) => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(storeAIImage.fulfilled, (state, action) => {
        state.uploading = false;
        // Check if media already exists to avoid duplicates
        const existingIndex = state.media.findIndex(media => media.id === action.payload.id);
        if (existingIndex === -1) {
          state.media = [action.payload, ...state.media];
          state.pagination.total += 1;
        } else {
          // Update existing media
          state.media[existingIndex] = action.payload;
        }
        // Don't save media data to localStorage for security
        console.log('AI image stored successfully:', action.payload);
      })
      .addCase(storeAIImage.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.error.message;
        console.error('AI image storage failed:', action.error);
      })
      
      // Update media
      .addCase(updateMedia.fulfilled, (state, action) => {
        const index = state.media.findIndex(media => media.id === action.payload.id);
        if (index !== -1) {
          state.media[index] = action.payload;
          // Don't save media data to localStorage for security
        }
      })

      // Delete media
      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.media = state.media.filter(media => media.id !== action.payload);
        state.pagination.total -= 1;
        state.selectedMedia = state.selectedMedia.filter(id => id !== action.payload);
        // Don't save media data to localStorage for security
      });
  }
});

export const {
  setFilters,
  setPage,
  clearFilters,
  setSelectedMedia,
  toggleMediaSelection,
  clearSelectedMedia,
  clearError
} = mediaSlice.actions;

export default mediaSlice.reducer;
